import React, { useState, useMemo } from "react";
import { EuiBreadcrumb, EuiPage, EuiPageBody } from "@elastic/eui";
import {
  ALL_OPSANIO_FEATURES,
  AnketaActionsEnum,
  AnketaAuthUser,
  AnketaRolesWithPermissions,
  hasPermission,
  Header,
  opsanioFeatureResource,
  OpsanioFeatureEnum,
  SideNav,
} from "../";
import { useLocation, useSearchParams } from "react-router-dom";
import { LayoutOutlet } from "src/pages/status";
import { parseWallSettings } from "./health/wall";
import { getOrgAttributeFlags } from "src/auth/functions/check-permissions";

export * from "./waves";
export * from "./versions";
export * from "./can";

/**
 * Per-feature opsanio access (RBAC): membership in an internal org
 * (`isMedocino || isAutonubil`) AND the editable matrix permission
 * `opsanio · <feature>` (admin =>Berechtigungen). Mirrors the backend
 * `getOpsanioFeatureAccess`, which re-enforces every request. `matrix` is the
 * live matrix from the auth context; omit it to use the hardcoded defaults.
 */
export const getOpsanioFeaturePermission = (
  user: AnketaAuthUser,
  feature: OpsanioFeatureEnum,
  matrix?: AnketaRolesWithPermissions,
): boolean =>
  getOrgAttributeFlags(user).isInternal &&
  hasPermission(
    user,
    opsanioFeatureResource(feature) as any,
    AnketaActionsEnum.view as any,
    undefined,
    matrix,
  );

/** Whether the opsanio section is visible at all - i.e. ANY feature is granted. */
export const getOpsanioPermissions = (
  user: AnketaAuthUser,
  matrix?: AnketaRolesWithPermissions,
): boolean =>
  ALL_OPSANIO_FEATURES.some((feature) =>
    getOpsanioFeaturePermission(user, feature, matrix),
  );

export const OpsanioHomePage = ({
  breadcrumbs,
}: {
  breadcrumbs: EuiBreadcrumb[];
}): JSX.Element => {
  const initialNavIsOpen = useMemo(() => {
    const stored = localStorage.getItem("anketa.nav.side.isOpen");
    return stored ? stored === "true" : false;
  }, []);

  const [navIsOpen, setNavIsOpen] = useState<boolean>(initialNavIsOpen);
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  // The health wall in kiosk mode (a wall screen) renders without header and
  // side nav. Only that page honours the flag.
  const kiosk =
    pathname === "/opsanio/health" && parseWallSettings(searchParams).kiosk;
  if (kiosk) return <LayoutOutlet />;

  return (
    <EuiPage>
      {navIsOpen && <SideNav />}
      <EuiPageBody>
        <Header
          breadcrumbs={breadcrumbs}
          navIsOpen={navIsOpen}
          setNavIsOpen={setNavIsOpen}
          updateCatalog={() => undefined}
        />
        {/* Crashes and unknown routes render inside the layout. */}
        <LayoutOutlet />
      </EuiPageBody>
    </EuiPage>
  );
};
